import React, { JSX, useCallback, useReducer, useState } from 'react';
import './AddressForm.css';
import { v4 } from 'uuid';
import { Button } from './Button/Button';
import { localized } from '../config/localization';
import { BlechconAddress } from '../blechcon';
import { Input } from './Input/Input';
import { CenteredRing } from './CenteredRing/CenteredRing';
import { formReducer } from './form/formReducer';
import { Checkbox } from './Checkbox/Checkbox';
import { findDistance } from '../services/api/blechconApi';

type Props = {
  readonly showUseForShippingCheckbox: boolean;
  readonly addressData: BlechconAddress;
  readonly onChangeAddress: (addressData: BlechconAddress) => void;
  readonly onRemoveAddress?: () => void;
  readonly onCancel?: () => void;
};

const twoChars = formatMessage(2);
const fourChars = formatMessage(4);

function min(limit: number, value: string) {
  return (value?.length || 0) < limit;
}

export function AddressForm({
  showUseForShippingCheckbox,
  addressData,
  onChangeAddress,
  onRemoveAddress,
  onCancel,
}: Props): JSX.Element {
  const [state, dispatch] = useReducer(formReducer, {
    values: {
      firstName: addressData.firstName,
      lastName: addressData.lastName,
      phone: addressData.phone,
      addressLine: addressData.addressLine,
      city: addressData.city,
      id: addressData.id || v4(),
      label: addressData.label,
      state: addressData.state,
      street: addressData.street,
      zip: addressData.zip,
      useForShipping: addressData.useForShipping ? 'true' : 'false',
    },
    errors: {
      firstName: null,
      lastName: null,
      phone: null,
      addressLine: null,
      city: null,
      label: null,
      state: null,
      street: null,
      zip: null,
    },
    valid: false,
  });

  const [isAddressValid, setIsAddressValid] = useState<boolean | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const handleAddress = useCallback(
    (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      setIsLoading(true);

      const a = {
        firstName: state.values.firstName,
        lastName: state.values.lastName,
        phone: state.values.phone,
        addressLine: state.values.addressLine,
        city: state.values.city,
        id: state.values.id,
        label: state.values.label,
        state: state.values.state,
        street: state.values.street,
        zip: state.values.zip,
        useForShipping: state.values.useForShipping === 'true',
      } as BlechconAddress;

      const addressLine = `${state.values?.street || ''} ${state.values?.zip || ''} ${state.values?.city || ''} ${
        state.values?.state || ''
      }`;

      findDistance(addressLine)
        .then((response) => {
          setIsAddressValid(true);
          onChangeAddress({
            ...a,
            ...response,
          });
        })
        .catch((error) => {
          console.log(error);
          setIsAddressValid(false);
        })
        .finally(() => {
          setIsLoading(false);
        });
    },
    [
      onChangeAddress,
      state.values.addressLine,
      state.values.city,
      state.values.firstName,
      state.values.id,
      state.values.label,
      state.values.lastName,
      state.values.phone,
      state.values.state,
      state.values.street,
      state.values.useForShipping,
      state.values.zip,
    ]
  );

  return (
    <div className="address-form">
      {isLoading ? (
        <div className="address-form__loading">
          <CenteredRing />
        </div>
      ) : (
        <form className="address-form__form" onSubmit={handleAddress}>
          <Input
            name="label"
            type="text"
            placeholder={localized.ADDRESS_LABEL}
            errors={state.errors}
            values={state.values}
            autoFocus
            onChange={(event) => {
              const value = event.target.value;
              dispatch({
                values: { label: value },
                errors: { label: min(2, value) ? twoChars : null },
              });
            }}
          />
          <Input
            name="addressLine"
            type="text"
            placeholder={localized.ADDRESS_LINE1}
            errors={state.errors}
            values={state.values}
            onChange={(event) => {
              const value = event.target.value;
              dispatch({
                values: { addressLine: value },
                errors: { addressLine: min(2, value) ? twoChars : null },
              });
            }}
          />
          <Input
            name="firstName"
            type="text"
            required={true}
            placeholder={localized.FIRST_NAME}
            errors={state.errors}
            values={state.values}
            onChange={(event) => {
              const value = event.target.value;
              dispatch({
                values: { firstName: value },
                errors: { firstName: min(2, value) ? twoChars : null },
              });
            }}
          />
          <Input
            name="lastName"
            type="text"
            required={true}
            placeholder={localized.LAST_NAME}
            errors={state.errors}
            values={state.values}
            onChange={(event) => {
              const value = event.target.value;
              dispatch({
                values: { lastName: value },
                errors: { lastName: min(2, value) ? twoChars : null },
              });
            }}
          />
          <Input
            name="phone"
            type="text"
            required={true}
            placeholder={localized.PHONE}
            errors={state.errors}
            values={state.values}
            onChange={(event) => {
              const value = event.target.value;
              dispatch({
                values: { phone: value },
                errors: { phone: min(2, value) ? twoChars : null },
              });
            }}
          />
          <Input
            name="street"
            type="text"
            placeholder={localized.ADDRESS_STREET}
            errors={state.errors}
            values={state.values}
            onChange={(event) => {
              const value = event.target.value;
              dispatch({
                values: { street: value },
                errors: { street: min(2, value) ? twoChars : null },
              });
            }}
          />
          <Input
            name="zip"
            type="text"
            placeholder={localized.ZIP}
            errors={state.errors}
            values={state.values}
            onChange={(event) => {
              const value = event.target.value;
              dispatch({
                values: { zip: value },
                errors: { zip: min(4, value) ? fourChars : null },
              });
            }}
          />
          <Input
            name="city"
            type="text"
            placeholder={localized.ADDRESS_CITY}
            errors={state.errors}
            values={state.values}
            onChange={(event) => {
              const value = event.target.value;
              dispatch({
                values: { city: value },
                errors: { city: min(2, value) ? twoChars : null },
              });
            }}
          />
          <Input
            name="state"
            type="text"
            placeholder={localized.ADDRESS_COUNTRY}
            errors={state.errors}
            values={state.values}
            onChange={(event) => {
              const value = event.target.value;
              dispatch({
                values: { state: value },
                errors: { state: min(2, value) ? twoChars : null },
              });
            }}
          />

          {showUseForShippingCheckbox && (
            <Checkbox
              label={localized.SHIPPING_ADDRESS_USE}
              checked={state.values.useForShipping === 'true'}
              onClick={(value) => {
                dispatch({
                  values: {
                    useForShipping: value ? 'true' : 'false',
                  },
                });
              }}
            />
          )}

          {isAddressValid === false && <p className="address-form__error">{localized.ADDRESS_NOT_VALID}</p>}

          <div className="address-form__actions">
            {onCancel && (
              <Button onClick={onCancel} secondary>
                {localized.ABORT}
              </Button>
            )}
            {onRemoveAddress && (
              <Button onClick={onRemoveAddress} dataCy="btn-remove" danger>
                {localized.REMOVE}
              </Button>
            )}
            <Button disabled={!state.valid} type="submit" dataCy="btn-save">
              {localized.SAVE}
            </Button>
          </div>
        </form>
      )}
    </div>
  );
}

function formatMessage(value: number): string {
  return localized.formatString(localized.MUST_CONTAIN_AT_LEAST_CHARACTERS, value) as string;
}
